import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClientPoint,
  useFloating,
  UseFloatingReturn,
  useInteractions,
  UseInteractionsReturn,
} from '@floating-ui/react';
import { debounce } from 'lodash-es';
import { AnimatePresence } from 'motion/react';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { TOTAL_HOURS } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { useZoomStore } from '../../store/zoom.store';
import { getHoverMinutesFromEvent, minutesToTimeStr } from '../../utils';
import { TooltipContainer } from './tooltip-container';

type UseTooltipReturn = {
  hoverMinutes?: number;
  hoveredEventId?: string;
  onMouseMove: (x: number, y: number, hoveredEventId?: string) => void;
  hideTooltip: () => void;
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  refs: UseFloatingReturn['refs'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
};

type HoverState = {
  hoverMinutes?: number;
  hoveredEventId?: string;
  x?: number;
  y?: number;
};

const STEADY_DELAY_MS = 700;
const MOVEMENT_EPSILON_PX = 1;
const HIDE_THRESHOLD_PX = 5;

export const useCalendarTooltip = (containerRef: React.RefObject<HTMLElement>): UseTooltipReturn => {
  const { pxPerMinute } = useZoomStore(['pxPerMinute']);
  const [hoverState, setHoverState] = useState<HoverState>({});
  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    elements: { reference: containerRef.current },
    middleware: [offset(20), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  // Explicit x/y (rather than the default automatic mouse tracking) so position only
  // updates at the throttled cadence below instead of re-rendering on every raw mousemove.
  const clientPoint = useClientPoint(context, { x: hoverState.x ?? null, y: hoverState.y ?? null });
  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint]);

  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const showTooltipDebounced = useMemo(
    () =>
      debounce((clientX: number, clientY: number, hoveredEventId?: string) => {
        const totalMinutes = getHoverMinutesFromEvent(clientY, containerRef.current as HTMLElement, pxPerMinute);
        setHoverState({ hoverMinutes: totalMinutes, hoveredEventId, x: clientX, y: clientY });
      }, STEADY_DELAY_MS),
    [containerRef, pxPerMinute]
  );
  const hideTooltip = useCallback(() => {
    setHoverState({});
    showTooltipDebounced.cancel();
  }, [showTooltipDebounced]);

  const onMouseMove = useCallback(
    (clientX: number, clientY: number, hoveredEventId?: string) => {
      const prev = lastPos.current;
      lastPos.current = { x: clientX, y: clientY };

      if (!prev) {
        hideTooltip();
        showTooltipDebounced(clientX, clientY, hoveredEventId);
        return;
      }

      const deltaX = Math.abs(prev.x - clientX);
      const deltaY = Math.abs(prev.y - clientY);
      const hasMovement = deltaX >= MOVEMENT_EPSILON_PX || deltaY >= MOVEMENT_EPSILON_PX;

      if (!hasMovement) {
        return;
      }

      const hasSignificantMovement = deltaX >= HIDE_THRESHOLD_PX || deltaY >= HIDE_THRESHOLD_PX;
      if (hasSignificantMovement && hoverState.hoverMinutes !== undefined) {
        hideTooltip();
      }

      showTooltipDebounced(clientX, clientY, hoveredEventId);
    },
    [hideTooltip, hoverState.hoverMinutes, showTooltipDebounced]
  );
  useEffect(() => () => showTooltipDebounced.cancel(), [showTooltipDebounced]);

  return {
    hoverMinutes: hoverState.hoverMinutes,
    hoveredEventId: hoverState.hoveredEventId,
    onMouseMove,
    hideTooltip,
    getReferenceProps,
    getFloatingProps,
    refs,
    floatingStyles,
  };
};

interface Props {
  hoverMinutes?: number;
  hoveredEventId?: string;
  containerRef: UseFloatingReturn['refs']['setFloating'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
}

const EventTooltipContent = ({ hoveredEventId }: { hoveredEventId: string }) => {
  const hoveredEvent = useCalendarGridStore((state) => state.eventsById[hoveredEventId]);
  const renderEventTooltipRef = useCalendarGridStore((state) => state.renderEventTooltipRef);

  if (!hoveredEvent) {
    return null;
  }

  if (renderEventTooltipRef?.current && typeof renderEventTooltipRef.current === 'function') {
    return renderEventTooltipRef.current(hoveredEvent) || null;
  }

  return (
    <div css={eventTooltipContentStyle}>
      <Text weight='bold' size='medium' color='white'>
        {hoveredEvent.eventType}
      </Text>
      <Text size='small' color='white'>
        {hoveredEvent.startTime} • {hoveredEvent.duration ?? 0}m
      </Text>
    </div>
  );
};

export const CalendarTooltip = memo(
  ({ hoverMinutes, hoveredEventId, containerRef, floatingStyles, getFloatingProps }: Props) => {
    const hoverTime = minutesToTimeStr(hoverMinutes ?? 0);
    const shouldShow = hoverMinutes !== undefined && hoverMinutes > 0 && hoverMinutes < TOTAL_HOURS * 60;

    return (
      <>
        <AnimatePresence>
          {shouldShow && (
            <FloatingPortal>
              <div
                ref={containerRef}
                style={floatingStyles}
                {...getFloatingProps()}
                css={{ pointerEvents: 'none' }}
                data-testid='calendar-tooltip'
              >
                <TooltipContainer>
                  {hoveredEventId ? <EventTooltipContent hoveredEventId={hoveredEventId} /> : hoverTime}
                </TooltipContainer>
              </div>
            </FloatingPortal>
          )}
        </AnimatePresence>
      </>
    );
  }
);
CalendarTooltip.displayName = 'CalendarTooltip';

const eventTooltipContentStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
};
